import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { authApi } from '../api/auth.js';
import { ApiError } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { homePathForRole } from '../auth/home-path.js';

export function MfaVerifyPage(): JSX.Element {
  const navigate = useNavigate();
  const { promote, clear } = useAuth();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await authApi.mfaVerify(code.trim());
      promote(res.token, res.expires_at, res.user);
      navigate(homePathForRole(res.user.role), { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_code') {
        setError('Codigo TOTP invalido.');
      } else if (err instanceof ApiError && err.code === 'mfa_not_configured') {
        navigate('/mfa/setup', { replace: true });
      } else {
        setError('No se pudo verificar el codigo.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-full items-center justify-center px-6 py-12">
      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        className="w-full max-w-sm space-y-5"
      >
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-semibold text-brand-900">Verificacion en dos pasos</h1>
          <p className="text-sm text-slate-500">
            Ingresa el codigo de 6 digitos de tu app de autenticacion
          </p>
        </div>

        <input
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          pattern="\d{6}"
          maxLength={6}
          required
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          className="w-full rounded-lg border border-slate-300 px-3 py-3 text-center tracking-[0.4em] font-mono text-lg focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          placeholder="123456"
        />

        {error && (
          <div
            role="alert"
            className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-200"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !/^\d{6}$/.test(code)}
          className="w-full rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700/90 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {submitting ? 'Verificando…' : 'Verificar'}
        </button>

        <button
          type="button"
          onClick={() => {
            clear();
            navigate('/login', { replace: true });
          }}
          className="w-full text-xs text-slate-500 hover:text-slate-700"
        >
          Cancelar y volver al login
        </button>
      </form>
    </main>
  );
}
